"use client";
import * as React from "react";
import {
  Box, Stack, Typography, Chip, IconButton, Tooltip,
} from "@mui/material";
import { Loader, Check, Eye, X } from "lucide-react";
import { useRunningSubagents, type RunningSubagentRow } from "../../chat/hooks/useRunningSubagents";
import { subagentLiveActivity } from "./Subagent/liveActivity";
import { cancelSubagent } from "./Subagent/cancelSubagent";
import { CHAT_COLUMN_MAX_WIDTH, CHAT_COLUMN_GUTTER } from "./layout";

function scrollToCard(jobId: string) {
  document.getElementById(`subagent-${jobId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
}

function Row({ row }: { row: RunningSubagentRow }) {
  const { part, terminal } = row;
  const activity = subagentLiveActivity(part) ?? (terminal ? null : "Working…");
  const ok = part.status === "succeeded";
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: "center", py: 0.5, minWidth: 0 }}
      data-testid="running-task-row"
    >
      {terminal ? (
        <Check size={14} color={ok ? undefined : "var(--mui-palette-error-main)"} />
      ) : (
        <Loader size={14} className="wp-spin" />
      )}
      <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
        {part.name || "Subagent"}
      </Typography>
      {activity && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}
        >
          {terminal ? (ok ? "done" : "failed") : activity}
        </Typography>
      )}
      <Tooltip title="View">
        <IconButton size="small" aria-label="View" data-ph="open" onClick={() => scrollToCard(part.job_id)}>
          <Eye size={14} />
        </IconButton>
      </Tooltip>
      {!terminal && part.status === "running" && (
        <Tooltip title="Cancel">
          <IconButton size="small" aria-label="Cancel" data-ph="cancel" onClick={() => cancelSubagent(part.job_id)}>
            <X size={14} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

/** Composer-anchored strip of the current chat's active subagents. Chat-scoped
 * and driven (via useRunningSubagents) by the same subagent_event-fed parts
 * the thread renders, so it never diverges from the in-thread SubagentCard. */
export default function RunningTasksStrip() {
  const rows = useRunningSubagents();
  const [expanded, setExpanded] = React.useState(false);
  if (rows.length === 0) return null;

  const activeCount = rows.filter((r) => !r.terminal).length;
  const collapsed = rows.length > 1 && !expanded;
  const collapsedLabel = activeCount >= 1
    ? `${activeCount} task${activeCount === 1 ? "" : "s"} running`
    : `${rows.length} task${rows.length === 1 ? "" : "s"} done`;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: CHAT_COLUMN_MAX_WIDTH,
        mx: "auto",
        px: CHAT_COLUMN_GUTTER,
        pb: 0.5,
      }}
      aria-live="polite"
      data-ph-scope="tasks"
    >
      {collapsed ? (
        <Chip
          icon={activeCount >= 1 ? <Loader size={14} className="wp-spin" /> : <Check size={14} />}
          label={collapsedLabel}
          data-ph="expand"
          onClick={() => setExpanded(true)}
          size="small"
          variant="outlined"
        />
      ) : (
        <Stack sx={{ minWidth: 0 }}>
          {rows.map((r) => (
            <Row key={r.part.job_id} row={r} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
